import React from 'react';

// Inline SVG artwork for the auth hero panel (screens/Login.png, screens/SignUp.png).
// Kept as code rather than exported images so colours stay in sync with auth.css.

const BLUE = '#1f6feb';
const BLUE_SOFT = '#dbe8ff';
const CARD = '#ffffff';
const LINE = '#c9d8f5';

const Defs = ({ id }) => (
  <defs>
    <linearGradient id={`${id}-tile`} x1="0" y1="0" x2="1" y2="1">
      <stop offset="0" stopColor="#5b9bff" />
      <stop offset="1" stopColor="#1f63e6" />
    </linearGradient>
    <linearGradient id={`${id}-glass`} x1="0" y1="0" x2="0" y2="1">
      <stop offset="0" stopColor="#ffffff" stopOpacity="0.95" />
      <stop offset="1" stopColor="#eef4ff" stopOpacity="0.9" />
    </linearGradient>
    <radialGradient id={`${id}-glow`} cx="0.5" cy="0.5" r="0.5">
      <stop offset="0" stopColor="#bcd4ff" stopOpacity="0.9" />
      <stop offset="1" stopColor="#bcd4ff" stopOpacity="0" />
    </radialGradient>
    <filter id={`${id}-shadow`} x="-30%" y="-30%" width="160%" height="160%">
      <feDropShadow dx="0" dy="10" stdDeviation="12" floodColor="#2c5fc9" floodOpacity="0.16" />
    </filter>
  </defs>
);

/** Floating square badge with an icon — used for the shield / doc / users / trend tiles. */
const Badge = ({ x, y, size, solid, filter, gradient, children }) => (
  <g transform={`translate(${x} ${y})`} filter={filter}>
    <rect width={size} height={size} rx={size * 0.24} fill={solid ? gradient : CARD} />
    {children}
  </g>
);

const UsersIcon = ({ cx, cy, color = BLUE }) => (
  <g fill={color}>
    <circle cx={cx - 5} cy={cy - 6} r="5.5" />
    <path d={`M${cx - 15} ${cy + 10} a10 9 0 0 1 20 0 z`} />
    <circle cx={cx + 7} cy={cy - 4} r="4.5" opacity="0.75" />
    <path d={`M${cx + 1} ${cy + 10} a8 7.5 0 0 1 15 0 z`} opacity="0.75" />
  </g>
);

export const SignInIllustration = () => {
  const id = 'ib-si';
  return (
    <svg className="ib-auth-illustration" viewBox="0 0 320 250" role="img" aria-label="InsureBox dashboard preview">
      <Defs id={id} />
      <ellipse cx="165" cy="215" rx="140" ry="26" fill={`url(#${id}-glow)`} />
      <ellipse cx="170" cy="150" rx="118" ry="86" fill="none" stroke={LINE} strokeDasharray="3 5" />

      {/* Dashboard window */}
      <g filter={`url(#${id}-shadow)`}>
        <rect x="40" y="48" width="200" height="150" rx="12" fill={`url(#${id}-glass)`} />
        <rect x="40" y="48" width="22" height="150" rx="10" fill={BLUE} />
        <circle cx="51" cy="68" r="3" fill="#8fb4ff" />
        <circle cx="51" cy="84" r="3" fill="#8fb4ff" />
        <circle cx="51" cy="100" r="3" fill="#8fb4ff" />
        <circle cx="72" cy="60" r="2.4" fill="#9db8e8" />
        <circle cx="80" cy="60" r="2.4" fill="#9db8e8" />
        <circle cx="88" cy="60" r="2.4" fill="#9db8e8" />
        <rect x="74" y="74" width="70" height="9" rx="4.5" fill={BLUE_SOFT} />
        <rect x="150" y="74" width="40" height="9" rx="4.5" fill={BLUE_SOFT} />
        {/* Bar chart */}
        <rect x="74" y="92" width="72" height="66" rx="8" fill={CARD} />
        <rect x="84" y="134" width="8" height="16" rx="2" fill="#7fa8ff" />
        <rect x="97" y="124" width="8" height="26" rx="2" fill="#5b8fff" />
        <rect x="110" y="114" width="8" height="36" rx="2" fill={BLUE} />
        <rect x="123" y="104" width="8" height="46" rx="2" fill={BLUE} />
        <path d="M84 128 L101 116 L114 120 L131 100" stroke="#9cc0ff" strokeWidth="2" fill="none" />
        {/* Donut */}
        <rect x="154" y="92" width="72" height="66" rx="8" fill={CARD} />
        <circle cx="190" cy="118" r="14" fill="none" stroke={BLUE_SOFT} strokeWidth="7" />
        <circle cx="190" cy="118" r="14" fill="none" stroke={BLUE} strokeWidth="7" strokeDasharray="60 88" transform="rotate(-90 190 118)" />
        <rect x="166" y="140" width="48" height="6" rx="3" fill={BLUE_SOFT} />
        <rect x="74" y="166" width="90" height="7" rx="3.5" fill={BLUE_SOFT} />
        <rect x="74" y="178" width="60" height="7" rx="3.5" fill={BLUE_SOFT} />
      </g>

      {/* Shield tile */}
      <Badge x="194" y="18" size="50" solid gradient={`url(#${id}-tile)`} filter={`url(#${id}-shadow)`}>
        <path d="M25 11 L36 15 V24 C36 31 31 36 25 39 C19 36 14 31 14 24 V15 Z" fill="none" stroke="#fff" strokeWidth="3" strokeLinejoin="round" />
        <path d="M20 25 L24 29 L31 21" fill="none" stroke="#fff" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
      </Badge>
      {/* Document tile */}
      <Badge x="252" y="90" size="42" filter={`url(#${id}-shadow)`}>
        <rect x="13" y="10" width="16" height="22" rx="3" fill={BLUE} />
        <rect x="16.5" y="16" width="9" height="2" rx="1" fill="#fff" />
        <rect x="16.5" y="21" width="9" height="2" rx="1" fill="#fff" />
        <rect x="16.5" y="26" width="6" height="2" rx="1" fill="#fff" />
      </Badge>
      {/* Users tile */}
      <Badge x="10" y="166" size="52" filter={`url(#${id}-shadow)`}>
        <UsersIcon cx={25} cy={26} />
      </Badge>
    </svg>
  );
};

export const SignUpIllustration = () => {
  const id = 'ib-su';
  const checkRow = (x, y, w) => (
    <g key={`${x}-${y}`}>
      <rect x={x} y={y} width="11" height="11" rx="3" fill={BLUE} />
      <path d={`M${x + 2.5} ${y + 5.5} l2.5 2.5 l4 -4.5`} stroke="#fff" strokeWidth="1.8" fill="none" strokeLinecap="round" strokeLinejoin="round" />
      <rect x={x + 17} y={y + 2.5} width={w} height="6" rx="3" fill={BLUE_SOFT} />
    </g>
  );
  return (
    <svg className="ib-auth-illustration" viewBox="0 0 320 250" role="img" aria-label="Account setup preview">
      <Defs id={id} />
      <ellipse cx="165" cy="220" rx="140" ry="24" fill={`url(#${id}-glow)`} />
      <ellipse cx="170" cy="150" rx="118" ry="86" fill="none" stroke={LINE} strokeDasharray="3 5" />

      {/* Profile card */}
      <g filter={`url(#${id}-shadow)`}>
        <rect x="46" y="44" width="170" height="160" rx="12" fill={`url(#${id}-glass)`} />
        <rect x="58" y="56" width="70" height="56" rx="8" fill={CARD} />
        <circle cx="80" cy="84" r="14" fill={BLUE} />
        <circle cx="80" cy="79" r="5" fill="#fff" />
        <path d="M71 93 a9 7 0 0 1 18 0 z" fill="#fff" />
        <rect x="100" y="76" width="22" height="5" rx="2.5" fill={BLUE_SOFT} />
        <rect x="100" y="86" width="16" height="5" rx="2.5" fill={BLUE_SOFT} />
        <rect x="134" y="70" width="62" height="7" rx="3.5" fill={BLUE_SOFT} />
        <rect x="134" y="84" width="44" height="7" rx="3.5" fill={BLUE_SOFT} />
        {checkRow(100, 128, 36)}
        {checkRow(100, 150, 30)}
      </g>

      {/* Checklist card */}
      <g filter={`url(#${id}-shadow)`}>
        <rect x="176" y="96" width="96" height="120" rx="12" fill={CARD} />
        <rect x="190" y="112" width="64" height="7" rx="3.5" fill={BLUE_SOFT} />
        <rect x="190" y="125" width="44" height="7" rx="3.5" fill={BLUE_SOFT} />
        {checkRow(190, 146, 44)}
        {checkRow(190, 168, 38)}
        {checkRow(190, 190, 30)}
      </g>

      {/* Trend tile */}
      <Badge x="208" y="18" size="56" solid gradient={`url(#${id}-tile)`} filter={`url(#${id}-shadow)`}>
        <path d="M13 38 L23 27 L31 33 L43 19" fill="none" stroke="#fff" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M35 19 H43 V27" fill="none" stroke="#fff" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round" />
      </Badge>
      {/* Users tile */}
      <Badge x="12" y="120" size="58" filter={`url(#${id}-shadow)`}>
        <UsersIcon cx={28} cy={29} />
      </Badge>
    </svg>
  );
};
